/**
 * The institutions catalog's search (TMMAPP-0163 U2, TMMAPI-0195 U2, MM-D-152) — wire types from
 * `src/api/generated/`. `GET /v1/institutions/search?q=&country=&scope=LOCAL|ALL` matches names and
 * aliases (and a typed 9-digit routing number or a BIC, exactly). `LOCAL` searches the member's
 * country, and with nothing there answers every country with `widened: true`.
 *
 * Only the name, place, country and kind are read: a routing number or a BIC is never part of an
 * option (the search item carries neither), so the picker can't show one to a member.
 */
import { request } from './client';
import type {
  InstitutionSearchItem as WireItem,
  InstitutionSearchResult as WireResult,
} from './generated';

export type InstitutionScope = 'LOCAL' | 'ALL';
export type InstitutionKind = WireItem['kind'];

export interface InstitutionOption {
  id: number;
  name: string;
  city: string | null;
  region: string | null;
  /** ISO 3166-1 alpha-2, upper case. */
  country: string;
  kind: InstitutionKind;
}

export interface InstitutionSearchResult {
  items: InstitutionOption[];
  /** Nothing in the member's country: the items are every country's ("Other countries"). */
  widened: boolean;
}

/** The server's minimum, once trimmed (shorter is `400 VALIDATION_FAILED`). */
export const INSTITUTION_QUERY_MIN = 2;

const KINDS: ReadonlySet<string> = new Set(['BANK', 'CREDIT_UNION', 'OTHER']);

/** An optional place part; blank reads as absent. */
function place(value: string | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : null;
}

/** An unknown kind reads `OTHER`. */
export function normalizeInstitutionItem(w: WireItem): InstitutionOption {
  return {
    id: w.id,
    name: w.name,
    city: place(w.city),
    region: place(w.region),
    country: w.country.toUpperCase(),
    kind: KINDS.has(w.kind) ? w.kind : 'OTHER',
  };
}

export function normalizeInstitutionSearch(w: WireResult): InstitutionSearchResult {
  return { items: w.items.map(normalizeInstitutionItem), widened: w.widened };
}

export const institutionsApi = {
  /**
   * GET /v1/institutions/search. `country` is the member's served country (`/me.user.country`),
   * which is also the server's default: sent so the search names the country it ran in. Never an
   * account's currency.
   */
  async search(
    q: string,
    { scope, country }: { scope: InstitutionScope; country: string | null },
  ): Promise<InstitutionSearchResult> {
    return normalizeInstitutionSearch(
      await request<WireResult>('GET', '/v1/institutions/search', {
        query: { q: q.trim(), scope, ...(country !== null ? { country } : {}) },
      }),
    );
  },
};
